import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Trophy, Fuel, Scale, Calendar, MapPin, User, Car, Clipboard, Calculator, Cloud, Wind, Droplet } from 'lucide-react';

const TANDA_LABELS = {
  entrenamiento_1: 'Entrenamiento 1',
  entrenamiento_2: 'Entrenamiento 2',
  clasificacion_1: 'Clasificación 1',
  clasificacion_2: 'Clasificación 2',
  serie: 'Serie',
  final: 'Final',
};
const TANDA_ORDER_WEEKEND = ['entrenamiento_1', 'entrenamiento_2', 'clasificacion_1', 'clasificacion_2', 'serie', 'final'];
const SALIDA_ORDER = Array.from({ length: 12 }, (_, i) => `salida_${i + 1}`);
const SALIDA_LABELS = SALIDA_ORDER.reduce((acc, k, i) => { acc[k] = `Salida ${i + 1}`; return acc; }, {});
const FUEL_DENSITY = 0.74;

const parseTimeMs = (t) => {
  if (!t) return null;
  const m = String(t).match(/^(\d+):(\d+)(?:[.,](\d+))?$/);
  if (!m) {
    const f = parseFloat(String(t).replace(',', '.'));
    return Number.isFinite(f) ? f * 1000 : null;
  }
  return (Number(m[1]) * 60 + Number(m[2]) + (m[3] ? Number('0.' + m[3]) : 0)) * 1000;
};

const computeTandaSummary = (tanda, baseWeight) => {
  const salida = Number(tanda?.fuel?.salida);
  const entrada = Number(tanda?.fuel?.entrada);
  const fast = Number(tanda?.fuel?.fast_laps) || 0;
  const slow = Number(tanda?.fuel?.slow_laps) || 0;
  const avgLaps = fast === 0 && slow === 0 ? null : fast + slow * 0.7;
  const consumo = Number.isFinite(salida) && Number.isFinite(entrada) ? +(salida - entrada).toFixed(2) : null;
  const consumoAvg = consumo !== null && avgLaps ? +(consumo / avgLaps).toFixed(3) : null;

  const base = tanda?.weights?.override ?? baseWeight;
  const pesoSalida = Number.isFinite(Number(base)) && Number.isFinite(salida)
    ? +(Number(base) + salida * FUEL_DENSITY).toFixed(1) : null;
  const pesoEntrada = Number.isFinite(Number(base)) && Number.isFinite(entrada)
    ? +(Number(base) + entrada * FUEL_DENSITY).toFixed(1) : null;

  return { best: tanda?.best_time || '—', consumo, avgLaps, consumoAvg, pesoSalida, pesoEntrada };
};

export default function EventSummary({ event }) {
  const baseWeight = event?.base_car_weight;
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcLaps, setCalcLaps] = useState('');

  const isTest = (event?.event_type || 'weekend') === 'test';
  const tandaOrder = isTest ? SALIDA_ORDER : TANDA_ORDER_WEEKEND;
  const tandaLabels = isTest ? SALIDA_LABELS : TANDA_LABELS;

  const summaries = useMemo(() => {
    return tandaOrder.map((key) => ({
      key,
      label: tandaLabels[key],
      data: event?.tandas?.[key] || {},
      ...computeTandaSummary(event?.tandas?.[key], baseWeight),
    }));
  }, [event, baseWeight]);

  const bestOverall = useMemo(() => {
    let best = null;
    for (const s of summaries) {
      const ms = parseTimeMs(s.best);
      if (ms !== null && (best === null || ms < best.ms)) best = { ms, tiempo: s.best, label: s.label };
    }
    return best;
  }, [summaries]);

  // Aggregate consumption stats across all tandas with valid data
  const aggregate = useMemo(() => {
    const valid = summaries.filter(s => s.consumoAvg != null);
    if (!valid.length) return null;
    const sumAvg = valid.reduce((a, s) => a + s.consumoAvg, 0);
    const sumTotal = valid.reduce((a, s) => a + (s.consumo || 0), 0);
    const sumLaps = valid.reduce((a, s) => a + (s.avgLaps || 0), 0);
    return {
      avgConsumoPorVta: +(sumAvg / valid.length).toFixed(3),
      totalConsumo: +sumTotal.toFixed(2),
      totalLaps: +sumLaps.toFixed(2),
      tandasCount: valid.length,
    };
  }, [summaries]);

  return (
    <div className="space-y-4 pb-12">
      {/* Event header card */}
      <Card className="bg-gradient-to-br from-red-950/30 to-neutral-900 border-red-900/30">
        <CardContent className="p-5">
          <h2 className="text-xl font-bold text-white mb-1">{event.circuit}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-400">
            <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{event.date}</div>
            <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{event.pilot}</div>
            <div className="flex items-center gap-1.5"><Car className="w-3.5 h-3.5" />{event.car}</div>
            <div className="flex items-center gap-1.5">
              <Scale className="w-3.5 h-3.5" />
              {baseWeight != null ? `${baseWeight} kg (base)` : 'Sin peso base'}
            </div>
          </div>
          {bestOverall && (
            <div className="mt-3 pt-3 border-t border-red-900/30 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-gray-400">{isTest ? 'Mejor vuelta del entrenamiento:' : 'Mejor vuelta del fin de semana:'}</span>
              <span className="font-mono text-lg text-green-400">{bestOverall.tiempo}</span>
              <Badge variant="outline" className="text-[10px]">{bestOverall.label}</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aggregate fuel summary */}
      {aggregate && (
        <Card className="bg-gradient-to-br from-yellow-950/30 to-neutral-900 border-yellow-900/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-yellow-300 flex items-center gap-2">
                <Fuel className="w-4 h-4" />
                {isTest
                  ? `Promedio del entrenamiento (${aggregate.tandasCount} ${aggregate.tandasCount === 1 ? 'salida' : 'salidas'} con datos)`
                  : `Promedio del fin de semana (${aggregate.tandasCount} ${aggregate.tandasCount === 1 ? 'tanda' : 'tandas'} con datos)`}
              </h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCalcOpen(true)}
                data-testid="open-fuel-calc"
                className="h-9 border-yellow-700 text-yellow-200 hover:bg-yellow-950"
              >
                <Calculator className="w-4 h-4 mr-1.5" />
                Calculadora
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-neutral-950 rounded p-2">
                <div className="text-[10px] text-gray-500 uppercase">Consumo / vta avg</div>
                <div className="font-mono text-yellow-400 text-lg" data-testid="agg-consumo-avg">
                  {aggregate.avgConsumoPorVta} <span className="text-xs text-gray-500">L</span>
                </div>
              </div>
              <div className="bg-neutral-950 rounded p-2">
                <div className="text-[10px] text-gray-500 uppercase">Total consumido</div>
                <div className="font-mono text-yellow-400 text-lg">
                  {aggregate.totalConsumo} <span className="text-xs text-gray-500">L</span>
                </div>
              </div>
              <div className="bg-neutral-950 rounded p-2">
                <div className="text-[10px] text-gray-500 uppercase">Vueltas avg totales</div>
                <div className="font-mono text-yellow-400 text-lg">{aggregate.totalLaps}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tandas grid — 2 cols on iPad portrait, 3 on landscape */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {summaries.map((s) => {
          const hasData = s.best !== '—' || s.consumo !== null || s.data?.observations || s.data?.changes_next;
          return (
            <Card
              key={s.key}
              className={`bg-neutral-900 border-neutral-800 ${!hasData ? 'opacity-40' : ''}`}
              data-testid={`summary-card-${s.key}`}
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-white">{s.label}</h3>
                  {s.best !== '—' && (
                    <Badge className="bg-green-700 text-[10px] font-mono">{s.best}</Badge>
                  )}
                </div>
                {/* Hora + weather chips */}
                {(s.data?.saved_at || s.data?.weather) && (
                  <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    {s.data?.saved_at && (
                      <span className="text-gray-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(s.data.saved_at).toLocaleString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    {s.data?.weather && (
                      <span className="text-gray-300 flex items-center gap-1">
                        <Cloud className="w-3 h-3 text-blue-400" />{s.data.weather.temp}°C
                        <Wind className="w-3 h-3 text-cyan-400 ml-1" />{s.data.weather.wind}
                        <Droplet className="w-3 h-3 text-blue-300 ml-1" />{s.data.weather.humidity}%
                      </span>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-neutral-950 rounded p-2">
                    <div className="text-gray-500 text-[10px] flex items-center gap-1"><Fuel className="w-3 h-3" />Consumo/vta</div>
                    <div className="font-mono text-yellow-400">
                      {s.consumoAvg != null ? `${s.consumoAvg} L` : '—'}
                    </div>
                  </div>
                  <div className="bg-neutral-950 rounded p-2">
                    <div className="text-gray-500 text-[10px] flex items-center gap-1"><Scale className="w-3 h-3" />Peso salida</div>
                    <div className="font-mono text-blue-400">
                      {s.pesoSalida != null ? `${s.pesoSalida} kg` : '—'}
                    </div>
                  </div>
                  <div className="bg-neutral-950 rounded p-2 col-span-2">
                    <div className="text-gray-500 text-[10px]">Vueltas (rápidas + lentas × 0.7)</div>
                    <div className="font-mono text-gray-300">
                      {s.avgLaps != null ? `${s.avgLaps} vta avg` : '—'}
                      {s.consumo != null && <span className="text-gray-500"> · Total: {s.consumo} L</span>}
                    </div>
                  </div>
                </div>

                {s.data?.observations && (
                  <div className="pt-2 border-t border-neutral-800">
                    <div className="text-[10px] text-gray-500 uppercase flex items-center gap-1">
                      <Clipboard className="w-3 h-3" />Observaciones
                    </div>
                    <p className="text-xs text-gray-300 whitespace-pre-wrap line-clamp-4">{s.data.observations}</p>
                  </div>
                )}
                {s.data?.changes_next && (
                  <div>
                    <div className="text-[10px] text-gray-500 uppercase">Cambios próxima</div>
                    <p className="text-xs text-amber-300 whitespace-pre-wrap line-clamp-3">{s.data.changes_next}</p>
                  </div>
                )}
                {!hasData && (
                  <p className="text-xs text-gray-600 italic">Sin datos cargados</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Fuel calculator */}
      <Dialog open={calcOpen} onOpenChange={setCalcOpen}>
        <DialogContent className="bg-neutral-900 border-neutral-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-yellow-400" />
              Calculadora de Combustible
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {!aggregate ? (
              <p className="text-xs text-gray-500">
                Cargá al menos una tanda con combustible y vueltas para usar la calculadora.
              </p>
            ) : (
              <>
                <div className="bg-neutral-950 rounded p-3 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Consumo / vta avg (promedio)</span>
                    <span className="font-mono text-yellow-400">{aggregate.avgConsumoPorVta} L</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Peso base auto + piloto</span>
                    <span className="font-mono text-blue-400">{baseWeight ?? '—'} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Densidad combustible</span>
                    <span className="font-mono text-gray-300">{FUEL_DENSITY} kg/L</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Cantidad de vueltas a correr</Label>
                  <Input
                    inputMode="decimal"
                    type="number"
                    step="0.1"
                    value={calcLaps}
                    onChange={(e) => setCalcLaps(e.target.value)}
                    placeholder="Ej: 25"
                    data-testid="calc-laps-input"
                    className="h-11 text-base bg-neutral-800 border-neutral-700 text-white font-mono"
                  />
                </div>
                {Number(calcLaps) > 0 && (() => {
                  const laps = Number(calcLaps);
                  const litros = +(laps * aggregate.avgConsumoPorVta).toFixed(2);
                  const fuelKg = +(litros * FUEL_DENSITY).toFixed(2);
                  const pesoSalida = baseWeight != null ? +(Number(baseWeight) + fuelKg).toFixed(2) : null;
                  const pesoRegreso = baseWeight != null ? +Number(baseWeight).toFixed(2) : null;
                  return (
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t border-neutral-800 text-center">
                      <div className="bg-neutral-950 rounded p-3">
                        <div className="text-[10px] text-gray-500 uppercase">Litros a cargar</div>
                        <div className="font-mono text-yellow-400 text-xl" data-testid="calc-result-litros">
                          {litros} <span className="text-xs text-gray-500">L</span>
                        </div>
                      </div>
                      <div className="bg-neutral-950 rounded p-3">
                        <div className="text-[10px] text-gray-500 uppercase">Peso salida</div>
                        <div className="font-mono text-blue-400 text-xl" data-testid="calc-result-salida">
                          {pesoSalida ?? '—'} <span className="text-xs text-gray-500">kg</span>
                        </div>
                      </div>
                      <div className="bg-neutral-950 rounded p-3">
                        <div className="text-[10px] text-gray-500 uppercase">Peso regreso (vacío)</div>
                        <div className="font-mono text-blue-400 text-xl" data-testid="calc-result-regreso">
                          {pesoRegreso ?? '—'} <span className="text-xs text-gray-500">kg</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
